import { RefObject, useEffect, useRef } from "react";
import { isHtmlElement, isInputOrTextareaElement } from "src/utility";

export interface useKeyboardShortcutsOptions {
	ignoreInputElements?: boolean;
	targetScope?: RefObject<HTMLElement>;
}

type ShortcutTrigger = {
	keyCode: string;
	alt?: boolean;
	shift?: boolean;
	ctrlOrCmd?: boolean;
};

export type KeyboardShortcutHandler = {
	triggers: [ShortcutTrigger, ...ShortcutTrigger[]];
	execute: () => void;
};

export function useKeyboardShortcuts(
	shortcutHandlers: Record<string, KeyboardShortcutHandler>,
	options?: useKeyboardShortcutsOptions,
) {
	const handlersRef = useRef(shortcutHandlers);
	handlersRef.current = shortcutHandlers;

	const optionsRef = useRef(options);
	optionsRef.current = options;

	useEffect(() => {
		const onKeyDown = (ev: KeyboardEvent) => {
			const handlers = handlersRef.current;

			if (targetIsIgnored(ev.target, optionsRef.current)) {
				return;
			}

			for (const [_id, handler] of Object.entries(handlers)) {
				for (const trigger of handler.triggers) {
					if (triggerIsActive(trigger, ev)) {
						ev.preventDefault();
						ev.stopPropagation();
						handler.execute();
						return;
					}
				}
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => {
			window.removeEventListener("keydown", onKeyDown);
		};
	}, []);
}

function triggerIsActive(
	{ keyCode, alt, shift, ctrlOrCmd }: ShortcutTrigger,
	ev: KeyboardEvent,
): boolean {
	return (
		keyCode === ev.code &&
		!!shift === ev.shiftKey &&
		!!alt === ev.altKey &&
		!!ctrlOrCmd === (ev.ctrlKey || ev.metaKey)
	);
}

function targetIsIgnored(
	target: EventTarget | null,
	options: useKeyboardShortcutsOptions | undefined,
): boolean {
	return (
		(!!options?.targetScope?.current &&
			isHtmlElement(target) &&
			!options.targetScope.current.contains(target)) ||
		(!!options?.ignoreInputElements && isInputOrTextareaElement(target))
	);
}
